import { useState } from 'react'

import { budgetEnVigueur } from '../../analysis/budgets'
import { nomCategorie } from '../../analysis/donnees'
import { enregistrerBudget, supprimerBudget } from '../../db/repo'
import { capitaliser, formatMois, moisCourant } from '../../domain/temps'
import type { Budget, Cents, Id } from '../../domain/types'
import {
  Bascule,
  Carte,
  Champ,
  ChampMontant,
  Encart,
  Entete,
  IconePoubelle,
  Montant,
  SelectCategorie,
  SelectCompte,
  useAction,
} from '../composants'
import { useDonnees } from '../donnees'

interface Brouillon {
  id?: Id
  categorieId: Id | null
  compteId: Id | null
  periode: 'mensuel' | 'annuel'
  montant: Cents | null
  debut: string
  fin: string
}

const vierge = (): Brouillon => ({ categorieId: null, compteId: null, periode: 'mensuel', montant: null, debut: moisCourant(), fin: '' })

export function PageBudgets() {
  const { d, ix } = useDonnees()
  const [brouillon, setBrouillon] = useState<Brouillon | null>(null)
  const { erreur, executer } = useAction()
  const mois = moisCourant()

  const actifs = d.budgets.filter((b) => budgetEnVigueur(b, mois))
  const autres = d.budgets.filter((b) => !budgetEnVigueur(b, mois))

  const enregistrer = () =>
    executer(async () => {
      if (!brouillon) return
      if (!brouillon.categorieId) throw new Error('Choisissez une catégorie.')
      if (!brouillon.montant) throw new Error('Indiquez un montant.')
      await enregistrerBudget({
        id: brouillon.id,
        categorieId: brouillon.categorieId,
        compteId: brouillon.compteId,
        periode: brouillon.periode,
        montant: brouillon.montant,
        debut: brouillon.debut,
        fin: brouillon.fin || null,
      })
      setBrouillon(null)
    })

  const ligne = (b: Budget) => (
    <li key={b.id}>
      <button
        type="button"
        className="item"
        onClick={() => setBrouillon({ id: b.id, categorieId: b.categorieId, compteId: b.compteId, periode: b.periode, montant: b.montant, debut: b.debut, fin: b.fin ?? '' })}
      >
        <div className="grandir">
          <div className="item__principal">{nomCategorie(ix, b.categorieId)}</div>
          <div className="item__secondaire">
            {b.periode === 'mensuel' ? 'par mois' : 'par année'} · {b.compteId ? ix.compteParId.get(b.compteId)?.nom : 'tous comptes'} · dès{' '}
            {formatMois(b.debut)}
            {b.fin ? ` jusqu’à ${formatMois(b.fin)}` : ''}
          </div>
        </div>
        <Montant valeur={b.montant} />
      </button>
    </li>
  )

  return (
    <>
      <Entete titre="Enveloppes" sous="Budgets par catégorie" retour />
      <main className="contenu">
        <Encart>
          Un budget se mesure <strong>à votre charge</strong> : une facture avancée puis partagée ne consomme que votre part. Il couvre la
          catégorie et ses sous-catégories.
        </Encart>

        {brouillon ? (
          <Carte titre={brouillon.id ? 'Modifier le budget' : 'Nouveau budget'}>
            <div className="pile">
              <Champ libelle="Catégorie">
                <SelectCategorie categories={d.categories} sens="depense" valeur={brouillon.categorieId} onChange={(c) => setBrouillon({ ...brouillon, categorieId: c })} />
              </Champ>
              <Bascule
                etiquette="Période"
                valeur={brouillon.periode}
                onChange={(p) => setBrouillon({ ...brouillon, periode: p })}
                options={[
                  { valeur: 'mensuel', libelle: 'Par mois' },
                  { valeur: 'annuel', libelle: 'Par année' },
                ]}
              />
              <Champ libelle="Montant (CHF)">
                <ChampMontant valeur={brouillon.montant} onChange={(m) => setBrouillon({ ...brouillon, montant: m })} />
              </Champ>
              <Champ libelle="Compte" aide="Limiter à un compte, ou suivre la catégorie sur tous les comptes.">
                <SelectCompte comptes={d.comptes} valeur={brouillon.compteId} onChange={(c) => setBrouillon({ ...brouillon, compteId: c })} tous="Tous les comptes" />
              </Champ>
              <div className="grille-2">
                <Champ libelle="Dès">
                  <input type="month" value={brouillon.debut} onChange={(e) => setBrouillon({ ...brouillon, debut: e.target.value })} />
                </Champ>
                <Champ libelle="Jusqu’à (facultatif)">
                  <input type="month" value={brouillon.fin} onChange={(e) => setBrouillon({ ...brouillon, fin: e.target.value })} />
                </Champ>
              </div>
              {erreur && <Encart ton="danger">{erreur}</Encart>}
              <div className="boutons">
                {brouillon.id && (
                  <button type="button" className="btn btn--danger" aria-label="Supprimer" onClick={() => void supprimerBudget(brouillon.id!).then(() => setBrouillon(null))}>
                    <IconePoubelle />
                  </button>
                )}
                <button type="button" className="btn grandir" onClick={() => setBrouillon(null)}>Annuler</button>
                <button type="button" className="btn btn--principal grandir" onClick={() => void enregistrer()}>Enregistrer</button>
              </div>
            </div>
          </Carte>
        ) : (
          <button type="button" className="btn btn--principal btn--bloc" onClick={() => setBrouillon(vierge())}>Nouveau budget</button>
        )}

        <Carte titre={`En vigueur — ${capitaliser(formatMois(mois))}`}>
          {actifs.length ? <ul className="liste">{actifs.map(ligne)}</ul> : <p className="tres-doux">Aucun budget en vigueur.</p>}
        </Carte>
        {autres.length > 0 && (
          <Carte titre="Passés ou à venir">
            <ul className="liste">{autres.map(ligne)}</ul>
          </Carte>
        )}
      </main>
    </>
  )
}
